/**
 * Swamithra — the session, and the destination a person was trying to reach.
 *
 * TWO PLATFORMS, TWO DIFFERENT PROMISES, ONE MODULE THAT KNOWS WHICH.
 *
 *   web    the JWT lives ONLY in an httpOnly, Secure, SameSite=Lax cookie the
 *          JavaScript never sees. Nothing token-shaped may appear in
 *          localStorage or sessionStorage (US-00-001.15, US-01-005.12) — so on
 *          web this module stores NOTHING and never sets a bearer header. The
 *          cookie rides along on `credentials: "include"` in ../api/client.
 *
 *   native there is no cookie jar the OS protects for an app, so the token is
 *          held in the Keychain / Keystore through `host().secureStorage`
 *          (US-00-001.3/.16/.17). Never AsyncStorage — that is an unencrypted
 *          file in the app sandbox.
 *
 * That asymmetry is why the decision is made HERE, once, rather than in each
 * app: an Expo app that got it wrong would look identical in QA and be a finding
 * in the VAPT report.
 */

import { touchSession } from "../api/auth"
import { setAccessToken, setSessionTokenHandler } from "../api/client"
import { SESSION_IDLE_MS } from "../domain/constants"
import { type Session } from "../domain/user"
import { host } from "../host"
import { logger } from "../logger"

/** Native only. Absent on web by design — see the header. */
const TOKEN_KEY = "swamithra.session_token"

/** Ordinary storage: a route is not a credential. */
const DESTINATION_KEY = "swamithra.pending_destination"

// -----------------------------------------------------------------------------
// Reading a JWT without verifying it
// -----------------------------------------------------------------------------

export type JwtLifetime = {
	issuedAt: Date
	expiresAt: Date
}

const BASE64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_"

/**
 * base64url → a byte-per-character string.
 *
 * Hand-rolled rather than `atob`, which is absent on some Hermes builds, and
 * rather than `Buffer`, which does not exist in a browser. Non-ASCII claim
 * VALUES come out as mojibake because the bytes are not UTF-8 decoded; the
 * structure survives, and the only claims read here are numbers.
 */
function decodeBase64Url(value: string): string | null {
	let bits = 0
	let buffer = 0
	let out = ""

	for (const char of value) {
		if (char === "=") continue
		const index = BASE64URL.indexOf(char)
		if (index === -1) return null
		buffer = (buffer << 6) | index
		bits += 6
		if (bits >= 8) {
			bits -= 8
			out += String.fromCharCode((buffer >> bits) & 0xff)
		}
	}

	return out
}

/**
 * `iat` and `exp` from a JWT.
 *
 * NO SIGNATURE CHECK, and none is wanted: the client cannot verify a signature
 * it has no key for, and pretending otherwise would invite someone to trust the
 * result. The server verifies on every request (API_STYLE_GUIDE.md §9). What
 * this is for is deciding, locally and without a round trip, whether to show the
 * splash gate or the OTP screen — a forged token gets a 401 on its first call
 * either way.
 *
 * Returns null for anything that is not three dot-separated segments with a
 * JSON payload carrying numeric `iat` and `exp`.
 */
export function readJwtLifetime(token: string): JwtLifetime | null {
	const segments = token.split(".")
	if (segments.length !== 3) return null

	const payload = segments[1]
	if (!payload) return null

	const decoded = decodeBase64Url(payload)
	if (decoded === null) return null

	let claims: unknown
	try {
		claims = JSON.parse(decoded)
	} catch {
		return null
	}

	if (typeof claims !== "object" || claims === null) return null
	const { iat, exp } = claims as { iat?: unknown; exp?: unknown }
	if (typeof iat !== "number" || typeof exp !== "number") return null
	if (exp <= iat) return null

	return { issuedAt: new Date(iat * 1000), expiresAt: new Date(exp * 1000) }
}

/** True when the token is past `exp`, or is not a token at all. */
export function isTokenExpired(token: string, now = new Date()): boolean {
	const lifetime = readJwtLifetime(token)
	if (!lifetime) return true
	return lifetime.expiresAt.getTime() <= now.getTime()
}

/**
 * True when more than half the token's life has elapsed.
 *
 * The trigger for a silent exchange (US-00-001.16). Half rather than "nearly
 * expired" because the exchange has to have time to fail and be retried before
 * the user is thrown back to an OTP screen — and because a token refreshed at
 * the halfway point of a five-day window is refreshed roughly every two and a
 * half days, which is not a load anyone will notice.
 */
export function isPastHalfLife(token: string, now = new Date()): boolean {
	const lifetime = readJwtLifetime(token)
	if (!lifetime) return false
	const midpoint =
		lifetime.issuedAt.getTime() +
		(lifetime.expiresAt.getTime() - lifetime.issuedAt.getTime()) / 2
	return now.getTime() >= midpoint
}

// -----------------------------------------------------------------------------
// The stored session
// -----------------------------------------------------------------------------

/** True on the platform that holds its own token. */
function holdsOwnToken(): boolean {
	return host().name !== "web"
}

/**
 * Adopts a session returned by OTP verification.
 *
 * On web the `accessToken` field is IGNORED — deliberately, and that is the
 * whole of US-00-001.15's client-side half: there is no code path on web that
 * writes a token anywhere a script can read it.
 */
export async function startSession(session: Session): Promise<void> {
	if (!holdsOwnToken()) return
	installSessionTokenHandler()
	if (!session.accessToken) {
		logger.warn("session response carried no access token on a native client")
		return
	}
	setAccessToken(session.accessToken)
	await host().secureStorage.set(TOKEN_KEY, session.accessToken)
}

export type RestoreResult = "restored" | "expired" | "none"

/**
 * Restores the token on launch, before anything renders.
 *
 *   restored  a valid token is in place; the splash gate advances straight to
 *             Home with no OTP screen (US-00-001.3)
 *   expired   the stored token is past `exp`; it has been CLEARED and the app
 *             goes to the entry screen — no crash, no blank screen
 *             (US-00-001.17)
 *   none      nothing was stored, or this is web, where the cookie is sent
 *             automatically and there is nothing to restore
 */
export async function restoreSession(): Promise<RestoreResult> {
	if (!holdsOwnToken()) return "none"
	// Before the first authenticated call, so a token renewed on it is kept.
	installSessionTokenHandler()

	const token = await host().secureStorage.get(TOKEN_KEY)
	if (!token) return "none"

	if (isTokenExpired(token)) {
		await endSession()
		return "expired"
	}

	setAccessToken(token)
	return "restored"
}

/**
 * Adopts a token the API renewed on a response header.
 *
 * THIS IS THE WHOLE OF THE SILENT EXCHANGE (US-00-001.16). There is no refresh
 * endpoint: the service mints a fresh JWT onto `X-Session-Token` when the one it
 * was given is past half its life, and any authenticated call may carry one. So
 * the client's job is not to ask — it is to notice, and to write what it was
 * given where this platform keeps tokens.
 *
 * On web there is nothing to write and this does nothing: the renewed cookie was
 * set by the same response and JavaScript must never hold a copy
 * (US-00-001.15).
 */
function adoptRenewedToken(token: string): void {
	if (!holdsOwnToken()) return
	setAccessToken(token)
	void host()
		.secureStorage.set(TOKEN_KEY, token)
		.catch((error: unknown) => {
			// The in-memory token is already updated, so the session survives this
			// app run either way; what is lost is the next launch.
			logger.warn("could not persist a renewed session token", {}, error)
		})
}

/**
 * Wires the renewal listener into the transport. Idempotent; call from the app
 * entry point after `installHost()`, or rely on `restoreSession()` doing it.
 */
export function installSessionTokenHandler(): void {
	setSessionTokenHandler(adoptRenewedToken)
}

/**
 * Makes the exchange happen for a token past half its life.
 *
 * Call on foreground and on launch. The renewal rides back on the response of an
 * ordinary authenticated request, so this makes the cheapest one there is and
 * lets `adoptRenewedToken` do the rest — invisible to the user, and no OTP
 * (US-00-001.16). A failure is swallowed: the existing token is still valid, so
 * carrying on and trying again beats signing out somebody who is signed in.
 */
export async function ensureFreshSession(): Promise<void> {
	if (!holdsOwnToken()) return
	installSessionTokenHandler()

	const token = await host().secureStorage.get(TOKEN_KEY)
	if (!token || isTokenExpired(token) || !isPastHalfLife(token)) return

	try {
		setAccessToken(token)
		await touchSession()
	} catch (error) {
		// A refusal has already been handled by the transport — it clears the
		// token and sends the person to sign-in. Anything else (offline, a
		// timeout) leaves a token that is still valid, so the right move is to
		// carry on and try again next time.
		logger.warn("session renewal call failed", {}, error)
	}
}

/**
 * Clears the local half of the session.
 *
 * The server half — invalidating the session so the same token replayed returns
 * 401 — is `signOut()` in ../api/auth, and BOTH have to happen (US-00-001.18).
 * This is also what a 401 handler calls before routing to OTP sign-in
 * (US-00-006.21).
 */
export async function endSession(): Promise<void> {
	setAccessToken(null)
	if (!holdsOwnToken()) return
	await host().secureStorage.remove(TOKEN_KEY)
}

/**
 * The idle deadline the client believes it is working to.
 *
 * Advisory only — the server slides the real one on every authenticated request
 * (US-00-001.14). Exposed so an app can show "signed in until…" without
 * inventing the five days itself.
 */
export function idleDeadlineFrom(lastRequestAt: Date): Date {
	return new Date(lastRequestAt.getTime() + SESSION_IDLE_MS)
}

// -----------------------------------------------------------------------------
// The held destination
// -----------------------------------------------------------------------------

/**
 * A WhatsApp deep link opened without a valid session must survive the OTP
 * round trip and land the person on the thing they tapped, not on Home
 * (US-00-001.20, US-00-002.21, US-00-004.18, US-01-004.15, US-01-005.24).
 *
 * IT IS DISCARDED AFTER ONE USE. `takeDestination` reads and clears in the same
 * call, so a second sign-in a week later cannot resurrect a link from the first.
 *
 * ONLY AN INTERNAL PATH IS HELD. The value comes from a deep link, which is
 * attacker-controllable: anything that is not a single-slash-prefixed path is
 * refused, so a crafted link cannot turn the post-login redirect into an open
 * redirect to another origin. `//evil.example` — protocol-relative, and a
 * genuine open redirect — is refused by the same rule.
 */
export function isSafeDestination(path: string): boolean {
	if (!path.startsWith("/")) return false
	if (path.startsWith("//")) return false
	if (path.length > 512) return false
	// A backslash is normalised to a slash by some routers, which turns
	// `/\evil.example` into a protocol-relative URL after normalisation.
	return !path.includes("\\")
}

/** Holds a destination across the sign-in round trip. Refuses an unsafe one. */
export async function holdDestination(path: string): Promise<boolean> {
	if (!isSafeDestination(path)) {
		logger.warn("refused to hold an unsafe post-login destination")
		return false
	}
	await host().storage.set(DESTINATION_KEY, path)
	return true
}

/**
 * Reads the held destination and clears it, in one call.
 *
 * Returns null when there is none, or when what was stored is no longer
 * acceptable — a check repeated on read because storage outlives the code that
 * wrote it.
 */
export async function takeDestination(): Promise<string | null> {
	const path = await host().storage.get(DESTINATION_KEY)
	await host().storage.remove(DESTINATION_KEY)
	if (!path || !isSafeDestination(path)) return null
	return path
}

/** Drops a held destination without using it. */
export async function clearDestination(): Promise<void> {
	await host().storage.remove(DESTINATION_KEY)
}

/**
 * Reads the held destination WITHOUT clearing it.
 *
 * For a screen that only shows what is held — `session-lapsed`, reached on its
 * own as a bookmark or a slow tab, not by the sign-in flow that consumes it.
 * Only `takeDestination` may spend it; a peek must not have that side effect,
 * or the entry screen would find nothing left to land on.
 */
export async function peekDestination(): Promise<string | null> {
	const path = await host().storage.get(DESTINATION_KEY)
	if (!path || !isSafeDestination(path)) return null
	return path
}
